<template>
    <vue-scroll>
        <div class="float-left">
            <el-button
                @click="create"
                type="primary"
                v-if="!readonly && can(Permissions.CREATE_USER)"
            >
                <i class="mdi mdi-plus"></i> Create
            </el-button>
            <el-input
                @input="onInputSearch"
                placeholder="Search Users..."
                style="width: 250px"
                v-model="currentQuery.filter.search"
                clearable
            />
        </div>
        <template v-if="filterable">
            <div class="float-right">
                Filter by:
                <el-select
                    style="width: 200px"
                    filterable
                    clearable
                    value-key="id"
                    v-model="filter.corporation"
                    placeholder="Corporation..."
                    @change="selectCorporation"
                >
                    <el-option
                        v-for="item in corporations.data"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                    />
                </el-select>
                <el-select
                    style="width: 200px"
                    filterable
                    clearable
                    value-key="id"
                    v-model="filter.community"
                    placeholder="Community..."
                    @change="selectCommunity"
                    v-if="communities.data.length"
                >
                    <el-option
                        v-for="item in filteredCommunities"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                    />
                </el-select>
                <el-select
                    style="width: 200px"
                    filterable
                    clearable
                    placeholder="Role..."
                    v-model="filter.role"
                    @change="selectRole"
                    v-if="hasRole(Roles.MAIN_ROLES)"
                >
                    <el-option-group label="Main Roles">
                        <el-option
                            v-for="role in MainRoles"
                            :key="role"
                            :label="getRoleName(role)"
                            :value="role"
                        />
                    </el-option-group>
                    <el-option-group label="Client Roles">
                        <el-option
                            v-for="role in ClientRoles"
                            :key="role"
                            :label="getRoleName(role)"
                            :value="role"
                        />
                    </el-option-group>
                </el-select>
            </div>
            <div class="clearfix"></div>
        </template>
        <div style="margin-top: 20px">
            <el-tabs
                type="card"
                v-model="activeTab"
                @tab-click="handleTabClick"
            >
                <el-tab-pane
                    label="Search Results"
                    name="search"
                    v-if="currentQuery.filter.search"
                >
                    <users-table
                        ref="tab_search"
                        :users="users.data"
                        :loading="users.meta.loading"
                        :pagination="users.meta.pagination"
                        :can-edit-user="canEditUser"
                        :can-delete-user="canDeleteUser"
                        :busy-items-prop="busyItems"
                        @sort-change="onSortChangeLocal"
                        @edit-item="editItem"
                        @delete-user="deleteUser"
                        @reset-password="resetPassword"
                        @copy-reset-url="copyResetUrl"
                        @current-change="onCurrentPageChangeLocal"
                    />
                </el-tab-pane>
                <el-tab-pane
                    label="Active Users"
                    name="active"
                    v-if="!currentQuery.filter.search"
                >
                    <users-table
                        ref="tab_active"
                        :users="users.data"
                        :loading="users.meta.loading"
                        :pagination="users.meta.pagination"
                        :can-edit-user="canEditUser"
                        :can-delete-user="canDeleteUser"
                        :busy-items-prop="busyItems"
                        @sort-change="onSortChangeLocal"
                        @edit-item="editItem"
                        @reset-password="resetPassword"
                        @copy-reset-url="copyResetUrl"
                        @current-change="onCurrentPageChangeLocal"
                        @delete-user="deleteUser"
                    />
                </el-tab-pane>
                <el-tab-pane
                    label="Inactive Users"
                    name="inactive"
                    v-if="!currentQuery.filter.search"
                >
                    <users-table
                        ref="tab_inactive"
                        :users="users.data"
                        :loading="users.meta.loading"
                        :pagination="users.meta.pagination"
                        :can-edit-user="canEditUser"
                        :can-delete-user="canDeleteUser"
                        :busy-items-prop="busyItems"
                        @sort-change="onSortChangeLocal"
                        @edit-item="editItem"
                        @reset-password="resetPassword"
                        @copy-reset-url="copyResetUrl"
                        @current-change="onCurrentPageChangeLocal"
                        @delete-user="deleteUser"
                    />
                </el-tab-pane>
                <el-tab-pane
                    label="Deactivated Users"
                    name="suspended"
                    v-if="!currentQuery.filter.search"
                >
                    <users-table
                        ref="tab_suspended"
                        :users="users.data"
                        :loading="users.meta.loading"
                        :pagination="users.meta.pagination"
                        :can-edit-user="canEditUser"
                        :can-delete-user="canDeleteUser"
                        :busy-items-prop="busyItems"
                        @sort-change="onSortChangeLocal"
                        @edit-item="editItem"
                        @reset-password="resetPassword"
                        @copy-reset-url="copyResetUrl"
                        @current-change="onCurrentPageChangeLocal"
                        @delete-user="deleteUser"
                    />
                </el-tab-pane>
            </el-tabs>
        </div>
        <el-dialog
            :close-on-click-modal="false"
            :title="editingItem ? 'Edit User' : 'Create User'"
            :visible.sync="dialogVisible"
            width="500px"
            v-if="!readonly || hasRole(Roles.CORPORATION_OWNER)"
        >
            <el-form @submit.prevent="submit()">
                <el-form-item required label="Name" :class="form.state('name')">
                    <el-input name="name" v-model="form.name" />
                    <form-feedback :form="form" field="name" />
                </el-form-item>
                <el-form-item
                    required
                    label="Email"
                    :class="form.state('email')"
                >
                    <el-input name="email" v-model="form.email" />
                    <form-feedback :form="form" field="email" />
                </el-form-item>
                <el-form-item
                    label="Phone Number"
                    :class="form.state('phone_number')"
                >
                    <el-input name="phone_number" v-model="form.phone_number" />
                    <form-feedback :form="form" field="phone_number" />
                </el-form-item>
                <el-form-item
                    label="Job Title"
                    :class="form.state('job_title')"
                >
                    <el-input name="job_title" v-model="form.job_title" />
                    <form-feedback :form="form" field="job_title" />
                </el-form-item>
                <el-form-item :class="form.state('username')" label="Username">
                    <el-input name="username" v-model="form.username" />
                    <form-feedback :form="form" field="username" />
                </el-form-item>
                <el-form-item
                    :class="form.state('password')"
                    :required="!editingItem"
                    label="Password"
                >
                    <el-input
                        autocomplete="new-password"
                        show-password
                        type="password"
                        v-model="form.password"
                    />
                    <form-feedback :form="form" field="password" />
                </el-form-item>
                <el-form-item
                    required
                    label="Role"
                    :class="form.state('role')"
                    v-if="!readonly"
                >
                    <el-select
                        filterable
                        placeholder="Select"
                        v-model="form.role"
                    >
                        <el-option-group label="Main Roles">
                            <el-option
                                v-for="role in MainRoles"
                                :key="role"
                                :label="getRoleName(role)"
                                :value="role"
                            />
                        </el-option-group>
                        <el-option-group label="Client Roles">
                            <el-option
                                v-for="role in ClientRoles"
                                :key="role"
                                :label="getRoleName(role)"
                                :value="role"
                            />
                        </el-option-group>
                    </el-select>
                    <form-feedback :form="form" field="role" />
                </el-form-item>
                <el-form-item
                    v-if="form.role === Roles.CORPORATION_OWNER"
                    label="Corporation"
                    :class="form.state('parent_id')"
                >
                    <el-select
                        filterable
                        clearable
                        v-model="form.parent_id"
                        placeholder="Select"
                    >
                        <el-option
                            v-for="item in corporations.data"
                            :key="item.id"
                            :label="item.name"
                            :value="item.id"
                        />
                    </el-select>
                    <form-feedback :form="form" field="parent_id" />
                </el-form-item>
                <el-form-item
                    :class="form.state('parent_ids')"
                    label="Community"
                    v-if="
                        form.role === Roles.COMMUNITY_MANAGER ||
                        form.role === Roles.COMMUNITY_ACCOUNTANT
                    "
                >
                    <select-community
                        :initial-options="initialCommunityOptions"
                        v-model="form.parent_ids"
                    />
                    <form-feedback :form="form" field="parent_ids" />
                </el-form-item>
                <el-row>
                    <el-col :span="12">
                        <el-form-item
                            label="Active"
                            :class="form.state('is_active')"
                        >
                            <el-switch v-model="form.is_active" />
                            <form-feedback :form="form" field="is_active" />
                        </el-form-item>
                    </el-col>
                    <el-col
                        :span="12"
                        class="text-right assignable-user"
                        v-if="
                            hasRole(Roles.MAIN_ROLES) &&
                            Roles.MAIN_ROLES.includes(form.role)
                        "
                    >
                        <el-form-item
                            label="Assignable"
                            :class="form.state('is_assignable')"
                        >
                            <el-switch
                                v-model="form.is_assignable"
                                class="d-inline-flex"
                            />
                            <form-feedback :form="form" field="is_assignable" />
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>
            <div slot="footer" class="dialog-footer">
                <div
                    class="float-left"
                    v-if="editingItem && can(Permissions.DELETE_USER)"
                >
                    <el-button
                        round
                        type="danger"
                        icon="mdi mdi-delete"
                        @click="deleteUser()"
                        :loading="isItemBusy(editingItem, 'delete')"
                    >
                        Delete User
                    </el-button>
                </div>
                <div class="float-right">
                    <el-button
                        @click="dialogVisible = false"
                        :disabled="form.busy"
                        >Cancel</el-button
                    >
                    <el-button
                        type="primary"
                        icon="mdi mdi-content-save"
                        @click="submit"
                        :loading="form.busy"
                        >Save
                    </el-button>
                </div>
                <div class="clearfix"></div>
            </div>
        </el-dialog>
    </vue-scroll>
</template>

<script>
import SelectCommunity from "@/components/SelectCommunity";
import UsersTable from "@/components/tables/UsersTable.vue";
import * as ClientRoles from "@/constants/client-roles";
import * as MainRoles from "@/constants/main-roles";
import * as Permissions from "@/constants/permissions";
import * as Roles from "@/constants/roles";
import * as Routes from "@/constants/routes";
import auth_mixin from "@/mixins/auth";
import backend_paginator from "@/mixins/backend-paginator"; // onSortChange, onCurrentPageChange, updateTableSorting, updateRoute
import unsaved_changes_mixin from "@/mixins/unsaved-changes";
import password_reset_mixin from "@/mixins/user/password-reset.js";
import _ from "lodash";
import qs from "qs";
import { mapState } from "vuex";

const INCLUDE = "roles,permissions";

function getData(params = {}) {
    //console.log(params);
    _.extend(params, {
        include: INCLUDE,
        all: 0
    });
    return window.axios.get(`/web-api/user?${qs.stringify(params)}`);
}

export default {
    components: {
        SelectCommunity,
        UsersTable
    },
    mixins: [
        auth_mixin,
        unsaved_changes_mixin,
        password_reset_mixin,
        backend_paginator
    ],

    props: {
        readonly: {
            type: Boolean,
            default: false
        },
        corporation: {
            type: Object,
            required: false
        },
        filterable: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            activeTab: "active",
            Roles,
            MainRoles,
            ClientRoles,
            dialogVisible: false,
            form: new Form({
                name: null,
                email: null,
                username: null,
                phone_number: null,
                role: null,
                is_active: true,
                is_assignable: false,
                parent_id: null,
                parent_ids: [],
                password: null,
                job_title: null
            }),
            editingItem: null,
            filter: {
                corporation: null,
                community: null,
                role: null
            },
            oldData: null,
            initialCommunityOptions: [],
            users: {
                data: [],
                meta: {
                    pagination: {},
                    loading: false
                }
            },
            currentQuery: {
                // corporation_id: null,
                // community_id: null,
                // role: null,
                filter: {
                    search: null,
                    status: "active"
                },
                page: 1,
                sort_by: "name",
                sort_order: "asc"
            },
            routeName: Routes.USERS
        };
    },
    mounted() {
        this.selectCorporationFromRoute();
        this.selectCommunityFromRoute();
        _.extend(
            this.currentQuery,
            _.pick(this.$route.query, ["sort_by", "sort_order", "page"])
        );
        _.extend(this.currentQuery.filter, _.get(this.$route, "query.filter"));
        this.activeTab = this.currentQuery.filter.status ?? "active";
        this.fetchUsers();
        Vue.nextTick(() => {
            this.updateTableSorting(this.activeTab);
        });
    },
    computed: {
        ...mapState(["corporations", "communities"]),
        filteredCommunities() {
            if (!this.filter.corporation) {
                return this.communities.data;
            }
            return this.communities.data.filter((item) => {
                return item.corporation_id === this.filter.corporation.id;
            });
        },
        hasChanges() {
            return (
                this.dialogVisible &&
                this.oldData !== JSON.stringify(this.getCleanFormData())
            );
        },
        editingItemHasCommunityManagerRole() {
            if (!this.editingItem) {
                return false;
            }
            return _.find(this.editingItem.roles.data, {
                role: Roles.COMMUNITY_MANAGER
            });
        },
        canEditUser() {
            return this.can(Permissions.EDIT_USER);
        },
        canDeleteUser() {
            return this.can(Permissions.DELETE_USER);
        }
    },
    methods: {
        onInputSearch: _.debounce(function () {
            if (Utils.emptyOrNull(this.currentQuery.filter.search)) {
                this.activeTab = "active";
            } else {
                this.activeTab = "search";
            }
            this.currentQuery.page = 1;
            this.currentQuery.filter.status = this.activeTab;
            this.updateRoute();
            this.fetchUsers();
        }, 500),
        selectCorporationFromRoute() {
            const corporationId = _.get(
                this.$route,
                "query.corporation_id",
                null
            );
            if (corporationId) {
                this.currentQuery.corporation_id = corporationId;
            }
            if (
                corporationId &&
                this.corporations.data &&
                this.corporations.data.length
            ) {
                const selected = this.corporations.data.find(
                    (item) => item.id == corporationId
                );
                if (selected) this.filter.corporation = selected;
            }
        },
        selectCommunityFromRoute() {
            const communityId = _.get(this.$route, "query.community_id", null);
            if (communityId) {
                this.currentQuery.community_id = communityId;
            }
            if (
                communityId &&
                this.communities.data &&
                this.communities.data.length
            ) {
                const selected = this.communities.data.find(
                    (item) => item.id == communityId
                );
                if (selected) this.filter.community = selected;
            }
        },
        selectCorporation(id) {
            this.currentQuery.corporation_id = id;
            this.updateRoute();
            this.fetchUsers();
        },
        selectCommunity(id) {
            this.currentQuery.community_id = id;
            this.updateRoute();
            this.fetchUsers();
        },
        selectRole(role) {
            this.currentQuery.role = role;
            this.updateRoute();
            this.fetchUsers();
        },
        setData(response) {
            this.users.data = response.data.data;
            this.users.meta.pagination = response.data.meta.pagination;
        },
        fetchUsers() {
            //console.log({ fetchUsers: this.currentQuery });
            this.users.meta.loading = true;
            getData(this.currentQuery)
                .then((response) => {
                    //console.log(response);
                    this.setData(response);
                })
                .finally(() => (this.users.meta.loading = false));
        },
        handleTabClick(tab) {
            this.currentQuery.filter.status = tab.name;
            this.currentQuery.page = 1;
            this.updateRoute();
            this.fetchUsers();
        },
        initOldData() {
            this.oldData = JSON.stringify(this.getCleanFormData());
        },
        getCleanFormData() {
            return _.pick(this.form, [
                "name",
                "email",
                "username",
                "is_active",
                "is_assignable",
                "phone_number",
                "role",
                "parent_id",
                "job_title"
            ]);
        },
        create() {
            this.initialCommunityOptions = [];
            this.editingItem = null;
            this.dialogVisible = true;
            this.form.clearFields();
            this.initOldData();
        },
        submit() {
            if (this.editingItem) {
                let fields = _.keys(this.form);
                if (this.editingItemHasCommunityManagerRole) {
                    fields = _.reject(_.keys(this.form), (key) =>
                        _.includes(["is_default_contact"], key)
                    );
                }
                window.App.put(
                    `/web-api/user/${this.editingItem.id}?include=${INCLUDE}`,
                    this.form,
                    null,
                    fields
                ).then((response) => {
                    this.dialogVisible = false;
                    this.$notify.success({
                        message: `Changes have been saved`
                    });
                    this.fetchUsers();
                });
            } else {
                window.App.post(
                    `/web-api/user?include=${INCLUDE}`,
                    this.form
                ).then((response) => {
                    this.dialogVisible = false;
                    this.$notify.success({
                        message: `New user has been saved.`
                    });
                    this.fetchUsers();
                });
            }
        },
        editItem(item) {
            this.form.clearFields();
            this.initialCommunityOptions = [];
            axios
                .get(`/web-api/user/${item.id}`, {
                    params: {
                        include: "roles.parent"
                    }
                })
                .then((response) => {
                    let user = response.data;
                    _.extend(
                        this.form,
                        _.pick(user, [
                            "name",
                            "email",
                            "username",
                            "is_active",
                            "is_assignable",
                            "phone_number",
                            "job_title"
                        ])
                    );
                    this.form.role = _.get(user, "roles.data.0.role");
                    if (this.form.role === Roles.COMMUNITY_MANAGER) {
                        _.forEach(user.roles.data, (role) => {
                            this.form.parent_ids.push(role.parent_id);
                            this.initialCommunityOptions.push(role.parent);
                        });
                    } else {
                        this.form.parent_id = _.get(
                            user,
                            "roles.data.0.parent_id"
                        );
                    }
                    this.editingItem = item;
                    this.dialogVisible = true;
                    this.initOldData();
                    this.fetchUsers();
                });
        },
        deleteUser(item) {
            this.$confirm(
                `Are you sure you want to delete this user?`,
                "Warning",
                {
                    confirmButtonText: "OK",
                    cancelButtonText: "Cancel",
                    type: "warning"
                }
            )
                .then(() => {
                    window.axios.delete(`/web-api/user/${item.id}`).then(() => {
                        this.$notify.success({
                            message: `User has been deleted`
                        });
                        this.fetchUsers();
                    });
                })
                .catch((error) => {
                    console.log(error);
                });
        },

        onSortChangeLocal(sort) {
            this.onSortChange(sort);
            this.fetchUsers();
        },
        onCurrentPageChangeLocal(page) {
            this.onCurrentPageChange(page);
            this.fetchUsers();
        },
        onSortChangeLocal({ prop, order }) {
            this.onSortChange({ prop, order });
            this.fetchUsers();
        },
        resetPassword(user) {
            // Use the mixin's sendPasswordReset method
            this.sendPasswordReset(user);
        },

        copyResetUrl(user) {
            axios
                .post(`/web-api/user/${user.id}/generate-reset-token`)
                .then((response) => {
                    const token = response.data.token;
                    // Use Vue Router to generate the URL
                    const resetUrl = `${window.location.origin}${
                        this.$router.resolve({
                            name: Routes.RESET_PASSWORD,
                            params: { token: token }
                        }).href
                    }`;

                    // Cross-browser clipboard copy
                    this.copyTextToClipboard(resetUrl);
                });
        },

        // Add this new method to handle cross-browser clipboard functionality
        copyTextToClipboard(text) {
            // Try using the modern Clipboard API first
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard
                    .writeText(text)
                    .then(() => {
                        this.$notify.success({
                            title: "Success",
                            message: "Reset URL copied to clipboard"
                        });
                    })
                    .catch((err) => {
                        console.error("Clipboard API failed:", err);
                        this.fallbackCopyTextToClipboard(text);
                    });
            } else {
                // Fall back to the older approach for browsers that don't support Clipboard API
                this.fallbackCopyTextToClipboard(text);
            }
        },

        fallbackCopyTextToClipboard(text) {
            // Create a temporary textarea element
            const textArea = document.createElement("textarea");
            textArea.value = text;

            // Make it invisible but still on the page
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            textArea.style.top = "-999999px";
            document.body.appendChild(textArea);

            // Select and copy
            textArea.focus();
            textArea.select();

            let successful = false;
            try {
                successful = document.execCommand("copy");
            } catch (err) {
                console.error("Fallback copy failed:", err);
            }

            // Clean up
            document.body.removeChild(textArea);

            if (successful) {
                this.$notify.success({
                    title: "Success",
                    message: "Reset URL copied to clipboard"
                });
            } else {
                this.$notify.warning({
                    title: "Copy Failed",
                    message: "Please copy this URL manually: " + text
                });
            }
        }
    },
    watch: {
        "filter.corporation"() {
            this.filter.community = null;
            this.currentQuery.community_id = null;
        },
        "corporations.data"() {
            this.selectCorporationFromRoute();
        },
        "communities.data"() {
            this.selectCommunityFromRoute();
        }
    }
};
</script>

<style>
.assignable-user .el-form-item__label {
    float: none;
}
.assignable-user .el-form-item__content {
    display: inline-block;
}
.default-user .el-form-item__label {
    float: none;
}

.default-user .el-form-item__content {
    display: inline-block;
}
</style>
